import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../config";

const TeamsIntegration = () => {
  const token = localStorage.getItem("accessToken");
  const headers = { Authorization: `Bearer ${token}` };
  const base = `${config.API_BASE_URL}/api/integrations`;

  // Role-based access: "user" role = monitor-only, no edit/add/remove
  const userInfo = useSelector(selectUserInfo);
  const isUserRole = userInfo?.role?.toLowerCase() === "user";

  // ─── State ──────────────────────────────────────────────────────────────
  const [connected, setConnected] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);

  // Teams / channels / chats
  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chats, setChats] = useState([]);

  // Selection form
  const [targetType, setTargetType] = useState("channel");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  // Multi-select: Set of target_ids
  const [checkedIds, setCheckedIds] = useState(new Set());

  // Saved configs — now a list
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // Loading states for dropdowns
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);

  // Chat pagination
  const [chatPage, setChatPage] = useState(0);
  const CHATS_PER_PAGE = 10;

  // ─── Check connection status ────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${base}/teams/status/`, { headers });
      setConnected(res.data.connected);
      setDisplayName(res.data.user_display_name || "");
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Fetch notification configs (list) ─────────────────────────────────
  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${base}/teams/notification-config/`, { headers });
      // API now returns an array
      setSavedConfigs(Array.isArray(res.data) ? res.data : []);
    } catch {
      // ignore
    }
  }, []);

  // ─── Listen for Teams OAuth result from the popup ─────────────────────
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== "teams_oauth_result" || !e.newValue) return;
      let result;
      try { result = JSON.parse(e.newValue); } catch { return; }
      localStorage.removeItem("teams_oauth_result");
      if (result.status === "success") {
        toast.success("Microsoft Teams connected successfully!");
        fetchStatus();
        fetchConfig();
      } else {
        toast.error("Microsoft Teams connection failed. Please try again.");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchStatus, fetchConfig]);

  useEffect(() => {
    fetchStatus();
  }, []);

  // ─── Connect ────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    try {
      const res = await axios.get(`${base}/teams/auth/`, { headers });
      const authUrl = res.data.auth_url;
      const w = 600, h = 700;
      const left = window.screenX + Math.round((window.outerWidth - w) / 2);
      const top  = window.screenY + Math.round((window.outerHeight - h) / 2);
      const popup = window.open(authUrl, "teams_oauth", `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`);
      if (!popup || popup.closed) window.location.href = authUrl;
    } catch {
      toast.error("Failed to start Teams connection");
    }
  };

  // ─── Disconnect ─────────────────────────────────────────────────────────
  const handleDisconnect = async () => {
    try {
      await axios.post(`${base}/teams/disconnect/`, {}, { headers });
      setConnected(false);
      setDisplayName("");
      setTeams([]);
      setChannels([]);
      setChats([]);
      setSavedConfigs([]);
      toast.success("Teams disconnected");
    } catch {
      toast.error("Failed to disconnect Teams");
    }
  };

  // ─── Fetch teams ────────────────────────────────────────────────────────
  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const res = await axios.get(`${base}/teams/teams/`, { headers });
      const list = res.data.teams || [];
      setTeams(list);
      if (list.length > 0) setSelectedTeamId(list[0].id);
    } catch {
      toast.error("Failed to fetch Teams");
    } finally {
      setLoadingTeams(false);
    }
  };

  // ─── Fetch channels ────────────────────────────────────────────────────
  const fetchChannels = async (teamId) => {
    if (!teamId) return;
    setLoadingChannels(true);
    try {
      const res = await axios.get(`${base}/teams/teams/${teamId}/channels/`, { headers });
      setChannels(res.data.channels || []);
    } catch {
      toast.error("Failed to fetch channels");
    } finally {
      setLoadingChannels(false);
    }
  };

  // ─── Fetch chats ───────────────────────────────────────────────────────
  const fetchChats = async () => {
    setLoadingChats(true);
    try {
      const res = await axios.get(`${base}/teams/chats/`, { headers });
      setChats(res.data.chats || []);
    } catch {
      toast.error("Failed to fetch chats");
    } finally {
      setLoadingChats(false);
    }
  };

  // Load resources
  useEffect(() => {
    fetchConfig();
    if (connected) { fetchTeams(); fetchChats(); }
  }, [connected, fetchConfig]);

  // Load channels when team selected
  useEffect(() => {
    if (selectedTeamId) {
      fetchChannels(selectedTeamId);
      setCheckedIds(new Set());
    }
  }, [selectedTeamId]);

  // ─── Toggle checkbox ────────────────────────────────────────────────────
  const toggleCheck = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── Add notification targets (multi) ──────────────────────────────────
  const handleAddTargets = async () => {
    if (checkedIds.size === 0) {
      toast.error("Please select at least one target");
      return;
    }
    setSaving(true);
    const alreadySaved = new Set(savedConfigs.map((c) => c.target_id));
    const itemsToAdd = targetType === "channel"
      ? channels.filter((c) => checkedIds.has(c.id) && !alreadySaved.has(c.id))
          .map((c) => ({ target_type: "channel", target_id: c.id, target_name: c.displayName, team_id: selectedTeamId }))
      : chats.filter((c) => checkedIds.has(c.id) && !alreadySaved.has(c.id))
          .map((c) => ({ target_type: "chat", target_id: c.id, target_name: c.topic || "Unnamed chat" }));

    if (itemsToAdd.length === 0) {
      toast.info("All selected targets are already added.");
      setSaving(false);
      return;
    }

    let added = 0;
    for (const payload of itemsToAdd) {
      try {
        const res = await axios.post(`${base}/teams/notification-config/`, payload, { headers });
        setSavedConfigs((prev) => [...prev, res.data]);
        added++;
      } catch (err) {
        const msg = err.response?.data?.error || `Failed to add "${payload.target_name}"`;
        toast.error(msg);
      }
    }
    if (added > 0) toast.success(`${added} notification target${added > 1 ? "s" : ""} added!`);
    setCheckedIds(new Set());
    setSaving(false);
  };

  // ─── Remove notification target ─────────────────────────────────────────
  const handleRemoveTarget = async (configId, targetName) => {
    setRemovingId(configId);
    try {
      await axios.delete(`${base}/teams/notification-config/${configId}/`, { headers });
      setSavedConfigs((prev) => prev.filter((c) => c.config_id !== configId));
      toast.success(`"${targetName}" removed`);
    } catch {
      toast.error("Failed to remove target");
    } finally {
      setRemovingId(null);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="py-6 px-10 rounded-3xl border border-blue-400 bg-white shadow-xl shadow-blue-50/50">
        <p className="text-gray-900 text-sm">Loading Teams status...</p>
      </div>
    );
  }

  // ── Monitor-only view for "user" role ────────────────────────────────────
  if (isUserRole) {
    return (
      <div className="py-6 px-10 rounded-3xl border border-blue-400 bg-white shadow-xl shadow-blue-50/50">
        <h3 className="text-2xl font-black mb-1 bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider border-b border-gray-100 pb-2">
          Microsoft Teams Notifications
        </h3>

        {/* Notification targets list */}
        <div className="space-y-2">
          <p className="text-sm font-bold text-gray-600 mb-1">
            Notification Targets
          </p>
          {savedConfigs.length === 0 ? (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-500">
              <span>No notification targets configured yet.</span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {savedConfigs.map((cfg) => (
                <div
                  key={cfg.config_id}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-100"
                >
                  <span className="text-lg select-none">
                    {cfg.target_type === "channel" ? "📢" : "💬"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{cfg.target_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{cfg.target_type}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${connected ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-700"}`}>
                    {connected ? "Connected" : "Disconnected"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-10 rounded-3xl border border-blue-400 bg-white shadow-xl shadow-blue-50/50">
      <h3 className="text-2xl font-black mb-3 bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider border-b border-gray-100 pb-2">
        Microsoft Teams Notifications
      </h3>

      {/* ── Connection Status ───────────────────────────────────────── */}
      {!connected ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-900">
            Connect your Microsoft Teams account to receive notifications when new integrations are created.
          </p>
          <button
            onClick={handleConnect}
            className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-blue-200 active:scale-95 text-sm tracking-wide"
          >
            Connect Microsoft Teams
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Connected indicator */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span>
              <span className="text-sm text-gray-900">Connected as <strong>{displayName}</strong></span>
            </div>
            <button onClick={handleDisconnect} className="px-4 py-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-all shadow-sm active:scale-95">
              Disconnect
            </button>
          </div>

          {/* Tab toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            {["channel", "chat"].map((type) => (
              <button
                key={type}
                onClick={() => { setTargetType(type); setCheckedIds(new Set()); setChatPage(0); }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  targetType === type ? "bg-blue-600 text-white shadow-sm" : "text-blue-700"
                }`}
              >
                {type === "channel" ? "Team Channel" : "Group Chat"}
              </button>
            ))}
          </div>

          {/* Channel — team + channels side by side, equal width */}
          {targetType === "channel" && (
            <div className="flex gap-4 items-start">
              {/* Left: Team dropdown */}
              <div className="space-y-1 w-72 flex-shrink-0">
                <label className="text-sm font-semibold text-gray-900">Team</label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  disabled={loadingTeams}
                  className="w-full h-9 px-3 bg-white border border-gray-300 text-gray-900 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:opacity-60 block"
                >
                  <option value="">{loadingTeams ? "Loading..." : "Select"}</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.displayName}</option>)}
                </select>
              </div>

              {/* Right: Channels checkbox list */}
              {selectedTeamId && (
                <div className="space-y-1 w-52 flex-shrink-0 ml-4">
                  <label className="text-sm font-semibold text-gray-900">Channels</label>
                  {loadingChannels ? (
                    <p className="text-xs text-gray-600 py-1">Loading...</p>
                  ) : channels.length === 0 ? (
                    <p className="text-xs text-gray-600 py-1">No channels</p>
                  ) : (
                    <div className="border border-blue-300 rounded-lg overflow-hidden">
                      {channels.map((ch) => {
                        const savedCfg = savedConfigs.find((s) => s.target_id === ch.id);
                        const isRemoving = savedCfg && removingId === savedCfg.config_id;
                        return (
                          <label key={ch.id} className="flex items-center gap-2 px-2.5 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0">
                            <input
                              type="checkbox"
                              checked={!!savedCfg || checkedIds.has(ch.id)}
                              onChange={() => savedCfg ? handleRemoveTarget(savedCfg.config_id, ch.displayName) : toggleCheck(ch.id)}
                              disabled={isRemoving}
                              className="w-3.5 h-3.5 accent-blue-600 cursor-pointer flex-shrink-0"
                            />
                            <span className="text-sm text-gray-900 flex-1 truncate">{ch.displayName}</span>
                            {isRemoving && <span className="text-sm text-gray-600">...</span>}
                            {savedCfg && !isRemoving && <span className="text-sm text-green-600 font-semibold">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Chat — 2-column grid with pagination */}
          {targetType === "chat" && (
            loadingChats ? (
              <p className="text-xs text-gray-600 py-1">Loading chats...</p>
            ) : chats.length === 0 ? (
              <p className="text-xs text-gray-600 py-1">No group or meeting chats found</p>
            ) : (() => {
              const totalPages = Math.ceil(chats.length / CHATS_PER_PAGE);
              const paginated = chats.slice(chatPage * CHATS_PER_PAGE, (chatPage + 1) * CHATS_PER_PAGE);
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-900">Group & Meeting Chats</label>
                    <span className="text-xs text-gray-400">{chats.length} chats</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {paginated.map((ch) => {
                      const savedCfg = savedConfigs.find((s) => s.target_id === ch.id);
                      const isRemoving = savedCfg && removingId === savedCfg.config_id;
                      return (
                        <label key={ch.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 border border-blue-100 rounded-lg bg-white transition">
                          <input
                            type="checkbox"
                            checked={!!savedCfg || checkedIds.has(ch.id)}
                            onChange={() => savedCfg ? handleRemoveTarget(savedCfg.config_id, ch.topic || "Unnamed chat") : toggleCheck(ch.id)}
                            disabled={isRemoving}
                            className="w-3.5 h-3.5 accent-blue-600 cursor-pointer flex-shrink-0"
                          />
                          <span className="text-sm text-gray-900 flex-1 truncate" title={ch.topic}>
                            {ch.chatType === "meeting" ? "📅 " : "💬 "}{ch.topic}
                          </span>
                          {isRemoving && <span className="text-sm text-gray-400">…</span>}
                          {savedCfg && !isRemoving && <span className="text-sm text-green-600 font-semibold">✓</span>}
                        </label>
                      );
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-8">
                      <button
                        onClick={() => setChatPage((p) => Math.max(0, p - 1))}
                        disabled={chatPage === 0}
                        className="px-4 py-1.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >← Previous</button>
                      <span className="text-sm font-medium text-gray-600">{chatPage + 1} / {totalPages}</span>
                      <button
                        onClick={() => setChatPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={chatPage === totalPages - 1}
                        className="px-4 py-1.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >Next →</button>
                    </div>
                  )}
                </div>
              );
            })()
          )}

          {/* Add button — only shown when items are checked */}
          {checkedIds.size > 0 && (
            <button
              onClick={handleAddTargets}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-5 rounded-full font-bold text-base transition-all shadow hover:shadow-blue-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : `Save ${checkedIds.size} Notification Target${checkedIds.size > 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamsIntegration;
